// vim: set ts=2 sts=2 sw=2 et:

import { connect } from "react-redux";
import { Dispatch } from "redux";

import {
  enterAttempt,
  markLift,
  overrideAttempt,
  overrideEntryId,
  setLiftingGroup,
} from "../../actions/liftingActions";
import { Entry, Flight, Lift } from "../../types/dataTypes";
import { GlobalState } from "../../types/stateTypes";
import { PlatformLiveFeatures } from "../live/platformLiveProfile";
import {
  captureResolvedRecordAttempt,
  clearRecordAttemptOverride,
  setRecordAttemptOverride,
} from "../live/platformLiveProfileActions";
import { PlatformRef } from "../types";
import type { WrpfRecordCategory } from "../handoff/types";
import { isValidPlatformRef } from "./platformRef";
import OperationLayerView from "./OperationLayerView";

/**
 * Cómo se muestra la pantalla de operación montada por OperatorLiveHost: completa en Levantamientos, como
 * barra de tarima en el resto de las pantallas, oculta en las de display. En los tres casos sigue en vivo.
 */
export type OperatorPresentation = "full" | "dock" | "hidden";

interface OwnProps {
  platformRef: PlatformRef | null;
  locationSearch: string;
  classicLiftingWindow?: boolean;
  liveFeatures?: PlatformLiveFeatures;
  presentation?: OperatorPresentation;
}

interface StateProps {
  state: GlobalState;
  assignedEntries: Array<Entry>;
  isValidPlatform: boolean;
  activeProfile: GlobalState["wrpfLive"]["activeProfile"];
}

interface DispatchProps {
  enterAttempt: (entryId: number, lift: Lift, attemptOneIndexed: number, weightKg: number) => void;
  markLift: (entryId: number, lift: Lift, attemptOneIndexed: number, success: boolean) => void;
  setLiftingGroup: (day: number, platform: number, flight: Flight, lift: Lift) => void;
  overrideAttempt: (attempt: number) => void;
  overrideEntryId: (entryId: number) => void;
  setRecordAttemptOverride: (attemptKey: string, category: WrpfRecordCategory | false) => void;
  clearRecordAttemptOverride: (attemptKey: string) => void;
  captureResolvedRecordAttempt: (attemptKey: string, category: WrpfRecordCategory | undefined) => void;
}

export type OperationLayerContainerProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: GlobalState, ownProps: OwnProps): StateProps => {
  const platformRef = ownProps.platformRef;
  const isValidPlatform = platformRef !== null && isValidPlatformRef(state.meet, platformRef);
  const assignedEntries =
    platformRef === null
      ? []
      : state.registration.entries.filter((entry) => {
          return entry.day === platformRef.day && entry.platform === platformRef.platformId;
        });

  return {
    state,
    assignedEntries,
    isValidPlatform,
    activeProfile: state.wrpfLive.activeProfile,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  enterAttempt: (entryId, lift, attemptOneIndexed, weightKg) =>
    dispatch(enterAttempt(entryId, lift, attemptOneIndexed, weightKg)),
  markLift: (entryId, lift, attemptOneIndexed, success) =>
    dispatch(markLift(entryId, lift, attemptOneIndexed, success)),
  setLiftingGroup: (day, platform, flight, lift) => dispatch(setLiftingGroup(day, platform, flight, lift)),
  overrideAttempt: (attempt) => dispatch(overrideAttempt(attempt)),
  overrideEntryId: (entryId) => dispatch(overrideEntryId(entryId)),
  setRecordAttemptOverride: (attemptKey, category) => dispatch(setRecordAttemptOverride(attemptKey, category)),
  clearRecordAttemptOverride: (attemptKey) => dispatch(clearRecordAttemptOverride(attemptKey)),
  captureResolvedRecordAttempt: (attemptKey, category) => dispatch(captureResolvedRecordAttempt(attemptKey, category)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OperationLayerView);
